import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axiosClient from "../../axios";
import LessonPage from "./LessonPage";
import QuizPage from "./QuizPage";
import LearnSingleSlug from "./CategoryContent";
import SiteFooter from "../../components/SiteFooter";

export default function LearnDispatcher() {
  const { slug1, slug2, slug3, slug4 } = useParams();
  const [taxonomy, setTaxonomy] = useState(null);
  const [termId, setTermId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchCategoriesAndBuildBreadcrumbs() {
      try {
        setLoading(true);
        setError(null);
        const res = await axiosClient.get("/api/fetch-categories");
        const terms = res.data.data.terms;

        const slugs = [slug1, slug2, slug3, slug4].filter(Boolean);
        const breadcrumbTrail = [
          { name: "Home", slug: "", icon: "bi-house" },
          { name: "Learn", slug: "learn", icon: "bi-journals" },
        ];

        let currentLevel = terms;
        let path = "learn";

        for (const slug of slugs) {
          const found = currentLevel.find((term) => term.slug === slug);
          if (!found) break;

          path += `/${found.slug}`;
          breadcrumbTrail.push({
            name: found.name,
            slug: path,
            icon:
              found.taxonomy === "video"
                ? "bi-play-circle"
                : found.taxonomy === "quiz"
                ? "bi-question-circle"
                : "bi-folder",
          });

          currentLevel = found.children || [];
          setTaxonomy(found.taxonomy || null);
          setTermId(found.id);
        }

        setBreadcrumbs(breadcrumbTrail);
      } catch (error) {
        console.error("Error building breadcrumbs", error);
        setError("Failed to load content. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    if (slug1) {
      fetchCategoriesAndBuildBreadcrumbs();
    } else {
      setBreadcrumbs([
        { name: "Home", slug: "", icon: "bi-house" },
        { name: "Learn", slug: "learn", icon: "bi-journals" },
      ]);
      setLoading(false);
    }
  }, [slug1, slug2, slug3, slug4]);

  if (loading) {
    return (
      <div className="d-flex flex-column" style={{ minHeight: "100vh" }}>
        <div className="container py-4">
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex flex-column" style={{ minHeight: "100vh" }}>
        <div className="container py-4">
          <div
            className="alert alert-danger d-flex align-items-center"
            role="alert"
          >
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <div>{error}</div>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column" style={{ minHeight: "100vh" }}>
      {/* Page content area */}
      <div className="d-flex flex-column flex-grow-1">
        {/* Enhanced Breadcrumbs */}
        <div className="bg-light border-bottom">
          <div className="container">
            <nav aria-label="breadcrumb" className="py-1">
              <div className="d-flex align-items-center">
                <ol className="breadcrumb mb-0">
                  {breadcrumbs.map((b, i) => (
                    <li
                      key={i}
                      className={`breadcrumb-item d-flex align-items-center ${
                        i === breadcrumbs.length - 1
                          ? "active text-dark fw-semibold"
                          : ""
                      }`}
                      aria-current={
                        i === breadcrumbs.length - 1 ? "page" : null
                      }
                    >
                      {i === breadcrumbs.length - 1 ? (
                        <span className="d-flex align-items-center">
                          <i className={`bi ${b.icon} me-2`}></i>
                          {b.name}
                        </span>
                      ) : (
                        <a
                          className="text-decoration-none text-primary d-flex align-items-center"
                          href={`/${b.slug}`}
                        >
                          <i className={`bi ${b.icon} me-2`}></i>
                          {b.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </nav>
          </div>
        </div>

        {/* Page Content */}
        <div className="container py-4 flex-grow-1">
          {taxonomy === "video" && <LessonPage termId={termId} />}
          {taxonomy === "quiz" && <QuizPage termId={termId} />}
          {taxonomy === null && <LearnSingleSlug />}
          {!["video", "quiz", null].includes(taxonomy) && (
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="bi bi-search display-4 text-muted"></i>
              </div>
              <h5 className="text-muted">Content Not Found</h5>
              <p className="text-muted">
                The requested learning content could not be found.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
